import { forwardRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { Checkbox, FormControlLabel, Grid } from '@material-ui/core'
import { Alert } from '@material-ui/lab'

import { Card, LoadingBox, ResponsibleCpfField } from 'components'

import { useFetch } from 'hooks'

import * as service from 'service'
import helpers from 'helpers'
import schema from './schema'

// Campos com useForm próprio: nascem só depois do GET, porque o defaultValues do RHF 5 não se atualiza depois.
const SebraeSettingsFields = forwardRef(
  ({ companyId, sebraeSettings, editionPermitted }, ref) => {
    const hasResponsableUser = !!sebraeSettings?.hasResponsableUser

    const { control, errors, getValues, triggerValidation } = useForm({
      validationSchema: schema,
      defaultValues: {
        beneficiarySebrae: sebraeSettings?.beneficiarySebrae || false,
        responsableUserCpf: helpers.formatters.cpf(
          sebraeSettings?.responsableUserCpf || '',
        ),
      },
    })

    const submit = async () => {
      const { beneficiarySebrae, responsableUserCpf } = getValues()

      return await service.dponet.companies.updateSebraeSettings({
        companyId,
        beneficiarySebrae,
        // Sem representante, o CPF não vai: a API devolveria 422.
        ...(hasResponsableUser && {
          responsableUserCpf: (responsableUserCpf || '').replace(/\D/g, ''),
        }),
      })
    }

    useImperativeHandle(ref, () => ({ validate: triggerValidation, submit }))

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Controller
            as={
              <FormControlLabel
                control={<Checkbox color="primary" />}
                labelPlacement="end"
                label="Beneficiário Sebrae"
                disabled={!editionPermitted}
              />
            }
            valueName="checked"
            onChange={([event]) => event.target.checked}
            name="beneficiarySebrae"
            control={control}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ResponsibleCpfField
            control={control}
            errors={errors}
            name="responsableUserCpf"
            disabled={!editionPermitted || !hasResponsableUser}
            helperText={
              hasResponsableUser ? 'Opcional' : 'Empresa sem representante'
            }
          />
        </Grid>
      </Grid>
    )
  },
)

// Seção "Sebrae Nacional" da edição de empresa: flag e CPF do representante pelo recurso de backoffice (D-05).
// O pai chama validate() antes do PUT e submit() depois dele (ver JuridicOrPersonalForm#handleEdit).
const SebraeSettingsForm = forwardRef(
  ({ companyId, editionPermitted }, ref) => {
    const { response, error, isLoading } = useFetch(
      service.dponet.companies.sebraeSettings,
      { companyId },
      [companyId],
    )

    return (
      <Card title="Sebrae Nacional">
        {isLoading && <LoadingBox />}
        {!isLoading && error && (
          <Alert severity="warning">
            Não foi possível carregar os dados do Sebrae. Eles não serão
            alterados ao salvar.
          </Alert>
        )}
        {!isLoading && !error && (
          <SebraeSettingsFields
            ref={ref}
            companyId={companyId}
            sebraeSettings={response?.data}
            editionPermitted={editionPermitted}
          />
        )}
      </Card>
    )
  },
)

SebraeSettingsFields.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  sebraeSettings: PropTypes.object,
  editionPermitted: PropTypes.bool,
}

SebraeSettingsForm.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  editionPermitted: PropTypes.bool,
}

export default SebraeSettingsForm
